import React from 'react';

/**
 * Device detection utilities
 */

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
}

/**
 * Detects if the current device is a mobile device
 */
export const isMobileDevice = (): boolean => {
  // Check user agent for mobile indicators
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/i;
  
  // Check screen size (mobile devices typically have smaller screens)
  const screenWidth = Math.min(window.screen.width, window.screen.height);
  const isMobileScreen = screenWidth <= 768;
  
  // Check viewport width
  const viewportWidth = window.innerWidth;
  const isMobileViewport = viewportWidth <= 768;
  
  // Check for touch capability
  const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Combine checks for more accurate detection
  const isMobileUA = mobileRegex.test(userAgent);
  
  // More comprehensive mobile detection
  return isMobileUA || (isMobileScreen && hasTouchScreen) || (isMobileViewport && hasTouchScreen);
};

/**
 * Detects if the current device is a tablet
 */
export const isTabletDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  const tabletRegex = /ipad|android(?=.*tablet)|tablet/i;
  const screenWidth = window.screen.width;
  
  // Tablets typically have screens between 768px and 1024px
  const isTabletScreen = screenWidth >= 768 && screenWidth <= 1024;
  
  return tabletRegex.test(userAgent) || isTabletScreen;
};

/**
 * Detects if the current device is a desktop
 */
export const isDesktopDevice = (): boolean => {
  return !isMobileDevice() && !isTabletDevice();
};

/**
 * Gets comprehensive device information
 */
export const getDeviceInfo = (): DeviceInfo => {
  const isMobile = isMobileDevice();
  const isTablet = isTabletDevice();
  const isDesktop = isDesktopDevice();
  
  return {
    isMobile,
    isTablet,
    isDesktop,
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
  };
};

/**
 * Hook for React components to get device info with updates on resize
 */
export const useDeviceDetection = () => {
  const [deviceInfo, setDeviceInfo] = React.useState<DeviceInfo>(getDeviceInfo);
  
  React.useEffect(() => {
    const handleResize = () => {
      setDeviceInfo(getDeviceInfo());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return deviceInfo;
};
